import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import { token } from '@atlaskit/tokens';
import SectionMessage from '@atlaskit/section-message';

import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import config from 'appconfig';
import { State } from 'model/State';
import t from 'modules/i18n/intl';
import { selectIsRenewableAccount } from 'modules/mac/duck/selectors';
import {
  atlassianCloudKeys,
  hostedAccountTypes,
  isCommercialOrAcademicOrStarterType,
  isHosted,
  isNotAutoRenewableDCKey,
  isSocialImpactOrOpenSourceLicenseType,
  isTrainingOrUniversityOrUniversityPricingPlanType,
  notPartOfForRenewKeys,
} from 'util/mac/helpers';
import { Dimensions } from 'components/visuals';
import { StyledFlex, StyledLink, StyledSenInfoWrapper } from '../StyledComponents';
import { messages } from '../../../messages';
import { ATLASSIAN_ORGANISATION } from 'data/products/keys';
import { HostedAccountsDetails } from './licensesRowHostedAccountsDetails';
import { ContactTypes } from '../licensesExpandedRow';
import AnnualDetailsSection from '../AnnualDetailsSection';
import NameEditableComponent from '../NameEditableComponent';
import { DetailsRow } from './DetailsRow';
import Tooltip from '../../../../../components/common/Tooltip';
import { UpdateAddressDetailsLink } from '../ActionsSectionElements/UpdateAddressDetailsLink';
import { removeTimezone } from 'modules/mac/common/utils';
import { StyledParagraphWithTextAlign, StyledSpanWithLineHeight } from '../../styledComponents';
import AppCouplingComponent from '../AppCouplingComponent';

const contactLink = (chunks) => <a href={config.companyContact}>{chunks}</a>;

const MainProductDetailsSection = ({ accountDetails }) => {
  const productPackage = accountDetails.merchandise.productPackage || accountDetails.productPackage;
  const { key, productType, pricingPlanProduct } = productPackage;
  const {
    merchandise: { details, productEdition },
    id: accountId,
    name: accountName,
    accountFriendlyName,
    latestPeriod,
    contactRelationships,
    renewalAction,
    creditCardData,
    accountUrl,
    isUnclaimedCloudSite,
    txAMigrated,
    accountPriceOverrideType,
    active,
    isAddOnAccount,
  } = accountDetails;

  const isRenewableAccount = useSelector((state: State) =>
    selectIsRenewableAccount(state, accountId),
  );

  const shouldShowAccountURL =
    key !== ATLASSIAN_ORGANISATION && accountDetails && (accountUrl || isUnclaimedCloudSite);

  const isAccountHosted = hostedAccountTypes.includes(productType);

  const showAutomaticallyChargedCardMessage =
    !atlassianCloudKeys.includes(key) && renewalAction === 'AUTO_RENEW' && creditCardData;

  const showContactUsToRenewMessage =
    !atlassianCloudKeys.includes(key) && renewalAction !== 'AUTO_RENEW' && isHosted(productType);

  const showContactUsForRenewal = new Date(latestPeriod?.endDate) > new Date();

  const hasAnnual =
    (pricingPlanProduct && latestPeriod.months === 12) || productEdition?.monthsValid === 12;

  const isMainBillingContactNotPartner = !!contactRelationships.find(
    ({ main, relationshipType, contact }) =>
      main && relationshipType === ContactTypes.BILLING && !contact.organisationDetails.partner,
  );
  const isAutoRenewable =
    active &&
    !isSocialImpactOrOpenSourceLicenseType(accountPriceOverrideType) &&
    isCommercialOrAcademicOrStarterType(details.licenseType) &&
    !isTrainingOrUniversityOrUniversityPricingPlanType(productType) &&
    isMainBillingContactNotPartner &&
    !notPartOfForRenewKeys.includes(key) &&
    !isNotAutoRenewableDCKey(key) &&
    isRenewableAccount;

  return (
    <StyledSenInfoWrapper>
      <DetailsRow
        label={<FormattedMessage {...messages.name} />}
        value={
          <NameEditableComponent
            accountId={accountId}
            accountName={
              accountFriendlyName ||
              accountName || (
                <FormattedMessage defaultMessage="Not yet named" id="mac.licenses.not-yet-named" />
              )
            }
            productType={productType}
          />
        }
        dataCy="mac-expanded-account-name"
      />
      <DetailsRow
        label={
          <>
            <FormattedMessage {...messages.SEN} />
            <Tooltip
              direction="up-middle"
              content={
                <StyledParagraphWithTextAlign $textAlign="center">
                  <FormattedMessage {...messages.senTooltip} />
                </StyledParagraphWithTextAlign>
              }
            >
              <StyledSpanWithLineHeight $marginLeft={`${Dimensions.units(0.5)}px`}>
                <QuestionCircleIcon
                  size="small"
                  color={token('color.icon.subtle', '#626F86')}
                  label="sen-tooltip"
                />
              </StyledSpanWithLineHeight>
            </Tooltip>
          </>
        }
        value={accountId ? `SEN-${accountId}` : ''}
        dataCy="mac-sen-details"
      />
      {shouldShowAccountURL && (
        <DetailsRow
          label={<FormattedMessage {...messages.URL} />}
          value={
            accountDetails && accountUrl === '' && isUnclaimedCloudSite ? (
              <FormattedMessage
                id="mac.licenses.site.not.claimed"
                defaultMessage="Site not yet claimed"
              />
            ) : (
              <StyledLink href={accountUrl}>{accountUrl}</StyledLink>
            )
          }
        />
      )}
      {hasAnnual && (
        <AnnualDetailsSection accountDetails={accountDetails} isAutoRenewable={isAutoRenewable} />
      )}
      {accountDetails && isAccountHosted && (
        <HostedAccountsDetails accountDetails={accountDetails} productKey={key} />
      )}

      {!txAMigrated && <UpdateAddressDetailsLink productKey={key} />}

      {showAutomaticallyChargedCardMessage && (
        <>
          <br />
          <SectionMessage>
            <FormattedMessage
              {...messages.automaticallyChargedCardMessage}
              values={{
                endDate: (
                  <FormattedDate
                    value={removeTimezone(latestPeriod?.endDate)}
                    day="2-digit"
                    month="long"
                    year="numeric"
                  />
                ),
              }}
            />
          </SectionMessage>
        </>
      )}

      {showContactUsToRenewMessage && (
        <StyledFlex width={`${Dimensions.units(12.5)}%`}>
          <SectionMessage>
            <FormattedMessage {...messages.notAutomaticallyRenewedProduct} />
            {showContactUsForRenewal && t(messages.contactToRenew, { contactLink })}
          </SectionMessage>
        </StyledFlex>
      )}

      {isAddOnAccount && <AppCouplingComponent appSen={accountId} />}
    </StyledSenInfoWrapper>
  );
};

export default MainProductDetailsSection;
